import { Injectable, Injector } from '@angular/core';
import { HawkerSessionService } from './hawker-session.service';
import {
  GridRowClass,
  GridRowData,
  GridValidatorFn,
  IModalDialogOptions,
  IModalDialogResult,
  IProduct,
  IWidgetAction,
  XuxGridAction,
} from '@shared/types';
import {
  assignIn,
  chain,
  get,
  isEmpty,
  isNil,
  keyBy,
  map,
  merge,
  size,
  toLower,
  toString,
  isEqual,
} from 'lodash';
import { lazyInject } from '@containers/base/decorators';
import { BehaviorSubject, Observable, rx, Subject } from '@app/shared/rx';
import { NumberFormatService } from '../number-format/number-format.service';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { calcCloseOutPreviewFields } from './hawker-session.utils';
import { GridApi } from 'ag-grid-community';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { setFormFieldValue } from '@app/components/form/services/schema-form/schema-form-functions';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';
import {
  HawkerSessionCloseOutPreviewModal,
  IHawkerSessionCloseOutPreviewModalOptions,
} from './hawker-session-close-out-modal.definition';
import { UiActions } from '@app/store/actions';
import { decimalNumber } from '@app/shared/common';
import { ProductService } from '../product/product.service';
import { COMMON_GENERAL_MESSAGE_CLASS } from '@app/shared/constants/common-warn-grid-setting';
import { DEFAULT_INFORMATION_DATA } from './hawker-session.constant';
import { BaseInjectService } from '../base';
import { cloneDataWithDepth, generateObjectId } from '@app/shared/utils';

@Injectable()
export class HawkerSessionCloseOutService extends BaseInjectService {
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(HawkerSessionService)
  public hawkerSessionService: HawkerSessionService;
  @lazyInject(ProductService)
  public productService: ProductService;

  public constructor(public injector: Injector) {
    super(injector);
  }

  public closeOutListGridApi: GridApi;
  public closeOutListXuxGrid: XuxEditGridComponent;
  public form: FormGroup;
  public closeOutPreviewGridSubject: Subject<IWidgetAction> = new Subject();
  public isCloseOutPreviewGridBusy$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public formValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true,
  );
  public getOrdersSummarySuccess$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);
  public displayedRowCount$: Subject<number> = new BehaviorSubject<number>(0);

  public closeOutPreviewDescriptionObs: Observable<string> =
    this.displayedRowCount$.pipe(
      rx.map((count) =>
        this.i18nService.translate(I18n.Forms.GridMessages.ShowingEntities, {
          count,
          entities: this.i18nService.translate(
            I18n.EntityNames.ProductSingularAndPlural,
          ),
        }),
      ),
    );

  public async promptModal(
    options: Partial<IHawkerSessionCloseOutPreviewModalOptions>,
  ): Promise<IModalDialogResult> {
    const modalOptions = this.buildModalOptions(options);
    const confirmed = await this.uiActions.promptFormModal({
      ...modalOptions,
      modalId: get(options, 'modalId', generateObjectId()),
    });

    return confirmed;
  }

  public buildModalOptions(
    options: Partial<IHawkerSessionCloseOutPreviewModalOptions>,
  ): IModalDialogOptions {
    return HawkerSessionCloseOutPreviewModal(this, {
      ...options,
      onFormReadyCB: this.getOnFormReady(options),
    } as IHawkerSessionCloseOutPreviewModalOptions);
  }

  public onModalClose(): void {
    this.form = null;
    this.formValid$.next(true);
    this.getOrdersSummarySuccess$.next(true);
    this.isCloseOutPreviewGridBusy$.next(false);
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public getOnFormReady(
    options: Partial<IHawkerSessionCloseOutPreviewModalOptions>,
  ): (form: FormGroup) => void {
    const { hawkerSessionId, hawkerSessionProducts } = options;

    return async (form: FormGroup): Promise<void> => {
      this.form = form;
      this.formValid$.next(form.valid);

      form.valueChanges.subscribe(() => {
        this.formValid$.next(form.valid);
      });

      setFormFieldValue(form, 'items', [], false);
      setFormFieldValue(form, 'hawker_session_id', hawkerSessionId, false);

      if (isNil(hawkerSessionId) || hawkerSessionId === '') {
        return;
      }

      this.isCloseOutPreviewGridBusy$.next(true);

      let sourceItems: GridRowData[] = [];
      let sourceInformationData: GridRowData = {};

      try {
        const response = await this.hawkerSessionService
          .getSaleDepletionOrdersSummary(hawkerSessionId)
          .toPromise();
        sourceItems = get(response, 'products', []);
        sourceInformationData = get(
          response,
          'information_data',
          DEFAULT_INFORMATION_DATA,
        );
        this.getOrdersSummarySuccess$.next(true);
      } catch (error: unknown) {
        const toast = {
          errors: {
            title: get(error, 'title', ''),
            message: get(error, 'meta.message', ''),
          },
        };
        this.getOrdersSummarySuccess$.next(false);
        this.uiActions.showErrorToasterMessage(toast);
      }

      const allProductsWithOrders = await this.buildProductsWithOrders({
        sourceItems,
        hawkerSessionProducts,
      });

      const previewItems = map(allProductsWithOrders, (product) =>
        this.getCloseOutPreviewItemMapper()(product),
      );

      setFormFieldValue(form, 'items', previewItems, false);
      setFormFieldValue(
        form,
        'original_items',
        cloneDataWithDepth(previewItems),
        false,
      );
      setFormFieldValue(
        form,
        'information_data',
        [sourceInformationData],
        false,
      );
      this.isCloseOutPreviewGridBusy$.next(false);
    };
  }

  private async buildProductsWithOrders(params: {
    sourceItems: GridRowData[];
    hawkerSessionProducts: IHawkerSessionCloseOutPreviewModalOptions['hawkerSessionProducts'];
  }): Promise<GridRowData[]> {
    const { sourceItems } = params;
    const hawkerSessionProducts = get(params, 'hawkerSessionProducts', {});
    const activeProducts: IProduct[] = get(
      hawkerSessionProducts,
      'activeProducts',
      [],
    );
    const inactiveProducts: IProduct[] = get(
      hawkerSessionProducts,
      'inactiveProducts',
      [],
    );
    const sourceItemsByEntityId = keyBy(sourceItems, 'product_entity_id');

    const mapProductWithSummary = (product: IProduct): IProduct => {
      const productEntityId = get(product, 'entity_id');
      const matchedSummary = sourceItemsByEntityId[productEntityId];

      return this.mergeProductSummary(product, matchedSummary);
    };
    const sortByName = (items: GridRowData[]): GridRowData[] =>
      chain(items)
        .sortBy((item) => {
          return toLower(toString(get(item, 'name')));
        })
        .value();

    const activeProductsWithOrders: GridRowData[] = chain(activeProducts)
      .thru((products: IProduct[]) => {
        return map(products, mapProductWithSummary);
      })
      .value();

    const inactiveProductsWithOrders: GridRowData[] = chain(inactiveProducts)
      .thru((products: IProduct[]) => {
        return map(
          products.filter((product) => {
            const productEntityId = get(product, 'entity_id');

            return !isNil(sourceItemsByEntityId[productEntityId]);
          }),
          mapProductWithSummary,
        );
      })
      .value();

    const hawkerProductsEntityIdMap = keyBy(
      [...activeProducts, ...inactiveProducts],
      'entity_id',
    );
    let unmatchedProducts: GridRowData[] = chain<GridRowData[]>(sourceItems)
      .filter((summaryItem) => {
        const summaryProductEntityId = get(summaryItem, 'product_entity_id');

        return isNil(hawkerProductsEntityIdMap[summaryProductEntityId]);
      })
      .map((summaryItem) => {
        return merge({}, summaryItem, {
          entity_id: get(summaryItem, 'product_entity_id'),
        });
      })
      .value();

    // Unmatched sale/depletion rows may not have names; backfill from Product master.
    unmatchedProducts =
      await this.mergeUnmatchedProductsWithNames(unmatchedProducts);

    // Final order requirement: (inactive + unmatched) asc by name, then active asc by name.
    const sortedInactiveAndUnmatched = sortByName([
      ...inactiveProductsWithOrders,
      ...unmatchedProducts,
    ]);
    const sortedActive = sortByName(activeProductsWithOrders);
    const allProductsWithOrders = [
      ...sortedInactiveAndUnmatched,
      ...sortedActive,
    ];

    return allProductsWithOrders;
  }

  private async mergeUnmatchedProductsWithNames(
    unmatchedProducts: GridRowData[],
  ): Promise<GridRowData[]> {
    if (isEmpty(unmatchedProducts)) {
      return unmatchedProducts;
    }

    const unmatchedProductEntityIds = Array.from(
      new Set(
        map(unmatchedProducts, (item) => {
          return get(item, 'entity_id') || get(item, 'product_entity_id');
        }).filter((entityId) => !isNil(entityId) && entityId !== ''),
      ),
    );

    if (isEmpty(unmatchedProductEntityIds)) {
      return unmatchedProducts;
    }

    const productNamesByEntityId = await this.fetchProductNamesByEntityIds(
      unmatchedProductEntityIds,
    );

    return map(unmatchedProducts, (item) => {
      const productEntityId =
        get(item, 'entity_id') || get(item, 'product_entity_id');
      const productName = get(productNamesByEntityId, productEntityId);

      if (isNil(productName) || productName === '') {
        return item;
      }

      return merge({}, item, {
        name: productName,
      });
    });
  }

  private async fetchProductNamesByEntityIds(
    productEntityIds: string[],
  ): Promise<Record<string, string>> {
    try {
      const response = await this.productService
        .fetchList({
          select: ['entity_id', 'name'],
          filter: `entity_id eq in(${productEntityIds.join(',')})`,
        })
        .toPromise();

      const products = get(response, 'items', []);

      return products.reduce(
        (nameMap, product) => {
          const entityId = get(product, 'entity_id');
          const name = get(product, 'name');

          if (!isNil(entityId) && !isNil(name) && name !== '') {
            nameMap[entityId] = name;
          }

          return nameMap;
        },
        {} as Record<string, string>,
      );
    } catch (error: unknown) {
      this.uiActions.showErrorToasterMessage({
        errors: {
          title: get(error, 'title', ''),
          message: get(error, 'meta.message', ''),
        },
      });

      return {};
    }
  }

  private mergeProductSummary(
    product: IProduct,
    matchedSummary: GridRowData,
  ): IProduct {
    return merge({}, product, {
      terminal_quantity: get(matchedSummary, 'terminal_quantity'),
      terminal_sales: get(matchedSummary, 'terminal_sales'),
      taxes: get(matchedSummary, 'taxes'),
    });
  }

  public getCloseOutPreviewItemMapper(): (item: any) => GridRowData {
    return (item: any): GridRowData => {
      return assignIn({}, item, calcCloseOutPreviewFields(item));
    };
  }

  /**
   * Returns a warning validator that checks if total returned qty exceeds total requested qty.
   * Does not block form submission — only triggers warning label and grid row highlighting.
   */
  public getTotalReturnedQtyOverflowWarningValidator(): GridValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const gridProductData: IProduct[] = control.value || [];

      if (isEmpty(gridProductData)) {
        return null;
      }

      const warningRows = chain(gridProductData)
        .filter((product) => {
          const returned = get(product, 'total_returned', 0);

          if (isNil(returned)) {
            return false;
          }

          return decimalNumber(get(product, 'total_requested', 0) ?? 0).lt(
            returned,
          );
        })
        .map((product) => get(product, 'entity_id'))
        .value();

      return isEmpty(warningRows)
        ? null
        : {
            returnedQtyOverflow: {
              data: warningRows,
              enableDefaultValidationRowClass: true,
              message:
                I18n.ErrorMessages.TotalQtyReturnedExceedsTotalQtyRequested,
            },
          };
    };
  }

  /**
   * Returns a warning validator that checks if a product was not requested.
   * Does not block form submission — only triggers warning label and grid row highlighting.
   */
  public getProductNotRequestedValidator(): GridValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const gridProductData: IProduct[] = control.value || [];

      if (isEmpty(gridProductData)) {
        return null;
      }

      const warningRows = chain(gridProductData)
        .filter((product) => {
          return !get(product, 'is_active', false);
        })
        .map((product) => get(product, 'entity_id'))
        .value();

      return isEmpty(warningRows)
        ? null
        : {
            productNotRequested: {
              data: warningRows,
              rowClass: GridRowClass.INFO_ROW,
              class: COMMON_GENERAL_MESSAGE_CLASS,
              message: I18n.ErrorMessages.ProductNotRequested,
              messageParams: {
                count: size(warningRows),
              },
            },
          };
    };
  }

  public getQtyRequestedAndQtyReturnedEmptyValidator(): GridValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const gridProductData: IProduct[] = control.value || [];

      if (isEmpty(gridProductData)) {
        return null;
      }

      const errorRows = chain(gridProductData)
        .filter((product) => {
          const requested = get(product, 'total_requested');
          const returned = get(product, 'total_returned', 0);

          return (
            isNil(requested) ||
            isEqual(requested, '') ||
            isNil(returned) ||
            isEqual(returned, '')
          );
        })
        .map((product) => get(product, 'entity_id'))
        .value();

      return isEmpty(errorRows)
        ? null
        : {
            returnQtyEmpty: {
              data: errorRows,
              enableDefaultValidationRowClass: true,
              message: I18n.ErrorMessages.QtyRequestedAndQtyReturnedAreRequired,
            },
          };
    };
  }

  public listModalGridAction(
    event: XuxGridAction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    grid: XuxEditGridComponent,
  ): void {
    this.closeOutListXuxGrid = grid;
    this.closeOutListGridApi = event.payload?.gridApi;
  }
}
